/**
 * @fileoverview ThemeSwitcher - Theme selection components and hook
 *
 * Purpose: Provides theme switching functionality (light/dark/system)
 * Dependencies: lucide-react for icons, localStorage for persistence
 * Used by: Navigation, settings, homepage
 *
 * Exports:
 * - useTheme: Hook for managing theme state
 * - ThemeSwitcher: Dropdown theme selector
 * - ThemeSwitcherHome: Icon-based theme buttons
 */

import { Sun, Moon, Monitor } from "lucide-react";
import { cn } from "@/utils/misc";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/ui/primitives";
import { useEffect, useRef, useState } from "react";

const themes = ["light", "dark", "system"] as const;

/**
 * Custom hook for managing theme state
 *
 * Features:
 * - Persists theme preference to localStorage
 * - Respects system preference when "system" is selected
 * - Applies theme class to document root
 * - Updates color-scheme meta for browser UI
 *
 * @returns {[Theme, (theme: Theme) => void]} Current theme and setter function
 *
 * @example
 * const [theme, setTheme] = useTheme();
 * // theme is "light" | "dark" | "system"
 * setTheme("dark");
 */
const useTheme = () => {
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark" | "system">(
    localStorage.theme || "system",
  );
  // Skip-first-run guard: the inline script in index.html's <head> has
  // already applied the stored theme by the time this mounts. A ref (not
  // state) so it needs no dependency and causes no extra render.
  const initializedRef = useRef(false);

  // A similar script is inlined in the <head> of index.html.
  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }
    if (currentTheme === "system") {
      localStorage.removeItem("theme");
    } else {
      localStorage.theme = currentTheme;
    }
    if (
      currentTheme === "dark" ||
      (currentTheme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches)
    ) {
      document.documentElement.classList.add("dark");
      document.documentElement.style.colorScheme = "dark";
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.style.colorScheme = "light";
    }
  }, [currentTheme]);

  return [currentTheme, setCurrentTheme] as const;
};

/**
 * ThemeSwitcher component - Dropdown theme selector
 *
 * Features:
 * - Shows current theme with corresponding icon
 * - Dropdown to select light/dark/system
 * - Persists selection via useTheme hook
 * - Customizable trigger styling
 *
 * @component
 * @example
 * <ThemeSwitcher />
 * <ThemeSwitcher triggerClass="w-full" />
 */
export function ThemeSwitcher({ triggerClass }: { triggerClass?: string }) {
  const [currentTheme, setCurrentTheme] = useTheme();
  return (
    <Select
      value={currentTheme}
      onValueChange={(theme) =>
        setCurrentTheme(theme as (typeof themes)[number])
      }
    >
      <SelectTrigger
        className={cn(
          "h-6 rounded border-primary/20 bg-secondary !px-2 hover:border-primary/40",
          triggerClass,
        )}
      >
        <div className="flex items-start gap-2">
          {currentTheme === "light" ? (
            <Sun className="h-[14px] w-[14px]" />
          ) : currentTheme === "dark" ? (
            <Moon className="h-[14px] w-[14px]" />
          ) : (
            <Monitor className="h-[14px] w-[14px]" />
          )}
          <span className="text-xs font-medium">
            {currentTheme.charAt(0).toUpperCase() + currentTheme.slice(1)}
          </span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {themes.map((theme) => (
          <SelectItem
            key={theme}
            value={theme}
            className={`text-sm font-medium text-primary/60 ${theme === currentTheme && "text-primary"}`}
          >
            {theme && theme.charAt(0).toUpperCase() + theme.slice(1)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const themeIcons = { light: Sun, dark: Moon, system: Monitor } as const;

/**
 * ThemeSwitcherSidebar - Compact icon toggle buttons for use in sidebars.
 * Three small buttons in a pill shape, active state highlighted.
 */
export function ThemeSwitcherSidebar() {
  const [currentTheme, setCurrentTheme] = useTheme();
  return (
    <div className="flex items-center gap-0.5 rounded-md border border-border bg-secondary/50 p-0.5">
      {themes.map((theme) => {
        const Icon = themeIcons[theme];
        const isActive = currentTheme === theme;
        return (
          <button
            key={theme}
            type="button"
            onClick={() => setCurrentTheme(theme)}
            className={cn(
              "rounded-sm p-1 transition-colors",
              isActive
                ? "bg-background text-primary shadow-sm"
                : "text-primary/40 hover:text-primary/60",
            )}
            title={theme.charAt(0).toUpperCase() + theme.slice(1)}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}

/**
 * ThemeSwitcherHome component - Icon-based theme selector
 *
 * Features:
 * - Three icon buttons for light/dark/system themes
 * - Hover state styling
 * - No dropdown, direct theme selection
 * - Ideal for homepage or landing pages
 *
 * @component
 * @example
 * <ThemeSwitcherHome />
 */
export function ThemeSwitcherHome() {
  const [currentTheme, setCurrentTheme] = useTheme();
  return (
    <div className="flex gap-3">
      {themes.map((theme) => (
        <button
          key={theme}
          name="theme"
          onClick={() => setCurrentTheme(theme)}
          className="transition-colors"
        >
          {theme === "light" ? (
            <Sun
              className={cn(
                "h-4 w-4 transition-colors",
                currentTheme === theme
                  ? "text-white"
                  : "text-white/60 hover:text-white/80",
              )}
            />
          ) : theme === "dark" ? (
            <Moon
              className={cn(
                "h-4 w-4 transition-colors",
                currentTheme === theme
                  ? "text-white"
                  : "text-white/60 hover:text-white/80",
              )}
            />
          ) : (
            <Monitor
              className={cn(
                "h-4 w-4 transition-colors",
                currentTheme === theme
                  ? "text-white"
                  : "text-white/60 hover:text-white/80",
              )}
            />
          )}
        </button>
      ))}
    </div>
  );
}
